import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { PLAYERS } from '../../../../theme/tokens';
import {
  ANY,
  DEFAULT_FILTERS,
  activeFilterCount,
  type ActivityFilter,
  type PlayerFilters,
  type RegisteredFilter,
  type SeenFilter,
  type SourceOption,
} from '../../roster-view';
import { useSourceLabel } from '../../useSourceLabel';
import {
  Panel,
  Grid,
  FieldRoot,
  FieldLabel,
  FieldControl,
  FieldValue,
  FieldChevron,
  FieldSelect,
  Footer,
  Summary,
  Actions,
  ClearButton,
} from './PlayersFilters.styles';

export type PlayersFiltersProps = {
  filters: PlayerFilters;
  onChange: (next: PlayerFilters) => void;
  /** Acquisition sources present in this brand's roster (with their counts), each affiliate on its own. */
  sources: SourceOption[];
  /** The brand's affiliates' names by number, to name each affiliate's entry (by number until read). */
  affiliateNames?: ReadonlyMap<string, string>;
  /** Rows matching the current filters, and the unfiltered roster size. */
  shown: number;
  total: number;
};

type Option = { value: string; label: string };

/** A pill dropdown: label above, native `<select>` overlaid on the styled face. */
const FilterField = ({
  label,
  value,
  options,
  onChange,
}: {
  /** English copy, used both as the visible label and as its i18n key. */
  label: string;
  value: string;
  options: readonly Option[];
  onChange: (value: string) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const current = options.find((option) => option.value === value) ?? options[0];
  return (
    <FieldRoot>
      {/* `label` arrives already-translated from the call site (the extractor only
          harvests literal call sites), so it renders as data, not as a key. */}
      <FieldLabel text={label} tag="span" />
      <FieldControl>
        <FieldValue>{current.label}</FieldValue>
        <FieldChevron>
          <Icon name="chevron-expand" size={14} />
        </FieldChevron>
        {/* A native <option> holds a string, not a node, so its copy comes through
            t() at the call site rather than <Text> (same exception as attributes). */}
        <FieldSelect value={value} aria-label={label} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FieldSelect>
      </FieldControl>
    </FieldRoot>
  );
};

/**
 * The Players filter tray: four funnel/attribution dropdowns over the merged roster.
 *
 * The dimensions follow the acquisition funnel the two data sets describe together
 * (registered → signed in → deposited → wagered) plus where each player came from:
 * on a live brand almost the whole roster sits at the top of that funnel, so a flat
 * list buries every question an owner actually has.
 *
 * The Source dropdown is data-driven: it lists only the sources this brand actually
 * has players from, so a brand is never offered a dead control. There is deliberately
 * NO test-account control even though the data set carries the flag, and the reasoning
 * is recorded at the foot of `roster-view.ts` (a brand owner has no test accounts, so
 * it would only invite them to hide their own customers). Filtering runs client-side
 * because the two data sets can only be filtered separately server-side, and neither
 * can filter on the other's fields.
 */
export const PlayersFilters = ({
  filters,
  onChange,
  sources,
  affiliateNames,
  shown,
  total,
}: PlayersFiltersProps): React.ReactElement => {
  const { t } = useTranslation();
  const sourceLabel = useSourceLabel(affiliateNames);

  // Options are built HERE, from literal `t('…')` calls, so the extractor sees every
  // string. A module-scope `{ value, label }` table run through `t(option.label)`
  // would render fine but never reach the locale catalogs.
  const activityOptions: Option[] = [
    { value: ANY, label: t('Everyone') },
    { value: 'betting', label: t('Has placed bets') },
    { value: 'depositors', label: t('Has deposited') },
    { value: 'dormant', label: t('No activity yet') },
  ];

  const registeredOptions: Option[] = [
    { value: ANY, label: t('Any time') },
    { value: '7d', label: t('Last 7 days') },
    { value: '30d', label: t('Last 30 days') },
    { value: '90d', label: t('Last 90 days') },
  ];

  const seenOptions: Option[] = [
    { value: ANY, label: t('Any time') },
    { value: '7d', label: t('Last 7 days') },
    { value: '30d', label: t('Last 30 days') },
    { value: 'lapsed', label: t('Over 30 days ago') },
    { value: 'never', label: t('Never signed in') },
  ];

  const sourceSelectOptions: Option[] = [
    { value: ANY, label: t('All sources') },
    // "Direct · 16" — the count makes an empty result predictable before selecting.
    ...sources.map((source) => ({ value: source.value, label: `${sourceLabel(source.value)} · ${source.count}` })),
  ];
  // A shared `?source=` link can name a source this brand has no players from. Keep
  // it in the list (with its real, zero count) rather than letting the control fall
  // back to showing "All sources" while the roster is in fact filtered to nothing.
  if (filters.source !== ANY && !sources.some((source) => source.value === filters.source)) {
    sourceSelectOptions.push({ value: filters.source, label: `${sourceLabel(filters.source)} · 0` });
  }

  const set = <K extends keyof PlayerFilters>(key: K, value: PlayerFilters[K]): void =>
    onChange({ ...filters, [key]: value });

  const count = activeFilterCount(filters);

  return (
    <Panel role="group" aria-label={t('Filter players')}>
      <Grid>
        <FilterField
          label={t('Activity')}
          value={filters.activity}
          options={activityOptions}
          onChange={(value) => set('activity', value as ActivityFilter)}
        />
        <FilterField
          label={t('Registered')}
          value={filters.registered}
          options={registeredOptions}
          onChange={(value) => set('registered', value as RegisteredFilter)}
        />
        <FilterField
          label={t('Last seen')}
          value={filters.seen}
          options={seenOptions}
          onChange={(value) => set('seen', value as SeenFilter)}
        />
        {/* One source means the dropdown can't narrow anything, so it stays hidden —
            unless a source filter IS active (a shared link), which must never be
            applied without a visible control to clear it. */}
        {sources.length > 1 || filters.source !== ANY ? (
          <FilterField
            label={t('Source')}
            value={filters.source}
            options={sourceSelectOptions}
            onChange={(value) => set('source', value)}
          />
        ) : null}
      </Grid>

      <Footer>
        <Summary
          text="Showing {{shown}} of {{total}} players"
          placeholders={{ shown: shown.toLocaleString('en-US'), total: total.toLocaleString('en-US') }}
          tag="span"
        />
        <Actions>
          {count > 0 ? (
            <ClearButton type="button" onClick={() => onChange(DEFAULT_FILTERS)}>
              <Icon name="close" size={13} color={PLAYERS.pageText} />
              {t('Clear filters')}
            </ClearButton>
          ) : null}
        </Actions>
      </Footer>
    </Panel>
  );
};

export default PlayersFilters;
